import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import Icon from "@mui/material/Icon";
import { toast } from "react-toastify";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import adminService from "services/adminService";
import { AssignableUser, RoleUser } from "../models";
import { SAVE_BUTTON_SX } from "shifts/shiftStyles";

// The picker searches server-side rather than filtering a preloaded list: there are ~51k
// employees, so there is no list small enough to hold in the page and still be complete.
const MIN_QUERY_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 300;

const fullName = (user: { firstName: string; lastName: string }) =>
  `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();

const RoleUsers = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const roleId = Number(id);

  const [roleName, setRoleName] = useState<string>("");
  const [members, setMembers] = useState<RoleUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [options, setOptions] = useState<AssignableUser[]>([]);
  const [selected, setSelected] = useState<AssignableUser[]>([]);
  const [query, setQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Guards against a slow early keystroke landing after a fast later one and repopulating the
  // list with results for a query the admin has already moved on from.
  const searchSeq = useRef(0);

  const loadMembers = useCallback(async () => {
    if (!roleId) return;
    setIsLoading(true);
    try {
      const [role, users] = await Promise.all([
        adminService.getRole(roleId),
        adminService.getRoleUsers(roleId),
      ]);
      setRoleName(role?.roleName ?? "");
      setMembers(users ?? []);
    } catch {
      toast.error("Could not load the users for this role.");
    } finally {
      setIsLoading(false);
    }
  }, [roleId]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  useEffect(() => {
    const term = query.trim();
    if (term.length < MIN_QUERY_LENGTH) {
      setOptions([]);
      return undefined;
    }

    const seq = ++searchSeq.current;
    setIsSearching(true);
    const timer = setTimeout(async () => {
      try {
        const results = await adminService.searchAssignableUsers(roleId, term);
        if (seq === searchSeq.current) setOptions(results ?? []);
      } catch {
        if (seq === searchSeq.current) setOptions([]);
      } finally {
        if (seq === searchSeq.current) setIsSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, roleId]);

  // Someone picked in this session is not yet a member, so the server's alreadyInRole is still
  // false for them. Filter them out here or they stay offered and can be added twice.
  const selectableOptions = useMemo(
    () => options.filter((o) => !selected.some((s) => s.employeeId === o.employeeId)),
    [options, selected]
  );

  const addSelected = async () => {
    if (selected.length === 0) return;
    setIsSaving(true);
    try {
      const result = await adminService.addRoleUsers(
        roleId,
        selected.map((u) => u.employeeId)
      );

      // Say what actually happened. "Saved" over a request that added nothing is how an admin
      // ends up believing a grant went through when it did not.
      const parts: string[] = [];
      if (result.added > 0) parts.push(`Added ${result.added} user${result.added === 1 ? "" : "s"}`);
      if (result.alreadyInRole > 0) parts.push(`${result.alreadyInRole} already had this role`);
      toast.success(parts.length > 0 ? parts.join(" · ") : "No changes were made.");

      (result.skipped ?? []).forEach((reason) => toast.warning(reason));

      setSelected([]);
      setQuery("");
      setOptions([]);
      await loadMembers();
    } catch {
      toast.error("Could not add those users to the role.");
    } finally {
      setIsSaving(false);
    }
  };

  const removeMember = async (user: RoleUser) => {
    try {
      await adminService.removeRoleUser(roleId, user.employeeId);
      toast.success(`Removed ${fullName(user)} from ${roleName}.`);
      await loadMembers();
    } catch {
      toast.error(`Could not remove ${fullName(user)}.`);
    }
  };

  return (
    <MDBox p={3}>
      <MDBox display="flex" alignItems="center" mb={1}>
        <Tooltip title="Back to roles">
          <IconButton onClick={() => navigate("/admin/roles")} size="small" sx={{ mr: 1 }}>
            <Icon>arrow_back</Icon>
          </IconButton>
        </Tooltip>
        <Typography variant="h5">{roleName ? `${roleName} — Users` : "Role Users"}</Typography>
      </MDBox>

      <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
        Search by name or email to add people to this role. Only employees with an email address
        can be added, because a role is matched to a person by their email when they sign in.
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <MDBox display="flex" alignItems="flex-start" gap={2} flexWrap="wrap">
          <Autocomplete
            multiple
            sx={{ flex: 1, minWidth: 360 }}
            value={selected}
            options={selectableOptions}
            filterOptions={(x) => x}          // the server already filtered; don't filter twice
            getOptionLabel={(option) => fullName(option)}
            isOptionEqualToValue={(a, b) => a.employeeId === b.employeeId}
            getOptionDisabled={(option) => option.alreadyInRole}
            loading={isSearching}
            noOptionsText={
              query.trim().length < MIN_QUERY_LENGTH
                ? `Type at least ${MIN_QUERY_LENGTH} characters`
                : "No matching employees with an email address"
            }
            onInputChange={(_, value, reason) => {
              if (reason === "input") setQuery(value);
            }}
            onChange={(_, value) => setSelected(value as AssignableUser[])}
            renderOption={(props, option) => (
              <li {...props} key={option.employeeId}>
                <MDBox>
                  <Typography variant="body2">
                    {fullName(option)}
                    {option.alreadyInRole && (
                      <Typography component="span" variant="caption" sx={{ ml: 1, color: "success.main" }}>
                        already in this role
                      </Typography>
                    )}
                    {!option.isActive && (
                      <Typography component="span" variant="caption" sx={{ ml: 1, color: "warning.main" }}>
                        inactive
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {option.email}
                    {option.title ? ` · ${option.title}` : ""}
                  </Typography>
                </MDBox>
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip {...getTagProps({ index })} key={option.employeeId} label={fullName(option)} />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Add users"
                placeholder="Name or email"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isSearching ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <MDButton
            variant="contained"
            sx={{ ...SAVE_BUTTON_SX, mt: 1 }}
            disabled={selected.length === 0 || isSaving}
            onClick={addSelected}
          >
            {isSaving ? "Adding…" : `Add ${selected.length || ""} ${selected.length === 1 ? "user" : "users"}`.trim()}
          </MDButton>
        </MDBox>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Job Title</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Remove</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {members.map((user) => (
              <TableRow key={user.employeeId} hover>
                <TableCell>{fullName(user)}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.title}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={user.isActive ? "Active" : "Inactive"}
                    color={user.isActive ? "success" : "default"}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={`Remove ${fullName(user)} from ${roleName}`}>
                    <IconButton size="small" onClick={() => removeMember(user)}>
                      <Icon fontSize="small">person_remove</Icon>
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {members.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Box sx={{ py: 2, textAlign: "center", color: "text.secondary" }}>
                    {isLoading ? "Loading…" : "Nobody has been given this role yet."}
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {members.length > 0 && (
        <Typography variant="caption" sx={{ mt: 1, display: "block", color: "text.secondary" }}>
          {members.length} user{members.length === 1 ? "" : "s"} in this role
        </Typography>
      )}
    </MDBox>
  );
};

export default RoleUsers;
